import * as React from 'react';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useEVData } from '../hooks/useEVData';
import AppNavbar from './AppNavbar';
import Header from './Header';
import MainGrid from './MainGrid';
import SideMenu from './SideMenu';
import AppTheme from './AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme';
import { AtomLoaderComponent } from './AtomLoaderComponent';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};


export default function Dashboard({ section = 'overview', ...props }) {
  const hasStartedInitialLoad = React.useRef(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const {
    loading,
    error,
    metrics,
    chartData,
    loadCSVData,
  } = useEVData();

  React.useEffect(() => {
    if (hasStartedInitialLoad.current) {
      return;
    }

    hasStartedInitialLoad.current = true;
    loadCSVData();
  }, [loadCSVData]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      {loading && !chartData?.tableData?.length && (
        <Box
          sx={(theme) => ({
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            backgroundColor: alpha(theme.palette.common.black, 0.52),
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
          })}
        >
          <AtomLoaderComponent />
          <Box sx={{ color: 'common.white', fontSize: '0.92rem', fontWeight: 500, textAlign: 'center' }}>
            Loading EV records and preparing dashboard visuals...
          </Box>
        </Box>
      )}

      <Box
        sx={(theme) => ({
          display: 'flex',
          minHeight: '100vh',
          background: theme.vars
            ? `radial-gradient(circle at top right, rgba(${theme.vars.palette.primary.mainChannel} / 0.14), transparent 20%), linear-gradient(180deg, rgba(${theme.vars.palette.background.defaultChannel} / 1) 0%, rgba(${theme.vars.palette.background.paperChannel} / 1) 100%)`
            : `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.14)}, transparent 20%), linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        })}
      >
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            position: 'relative',
            backgroundColor: 'transparent',
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              pt: { xs: 1, md: 3 },
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header
              searchQuery={searchQuery}
              onSearchChange={(event) => setSearchQuery(event.target.value)}
            />
            <MainGrid
              section={section}
              searchQuery={searchQuery}
              loading={loading}
              error={error}
              metrics={metrics}
              chartData={chartData}
              onRetry={loadCSVData}
            />
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
